import { createSelector } from '@reduxjs/toolkit'
import { useT } from '@transifex/react'
import { isFinite, subtract } from 'vend-number'
import {
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../../../../functions/customer'
import { containsGiftCard } from '../../../../functions/lineItem'
import { isABPAddedButNotPaid } from '../../../../functions/payments'
import {
  getOutstandingBalance,
  hasPayments,
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccountLimitReached,
  isValidCustomer,
} from '../../../../functions/sale'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectPayAccountBalance,
  selectSaleCustomer,
  selectSaleFinals,
  selectSalePayments,
  selectSaleStatus,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../../../state/store'
import { getFeatureFromCache } from '../../../../util/feature-cache'

const dataSelector = createSelector(
  [
    selectSaleStatus,
    selectSaleFinals,
    selectSalePayments,
    selectSaleCustomer,
    selectLineItems,
    selectPayAccountBalance,
    (state: RootState) => state.currentSale.customerBalances,
    (state: RootState) => state.currentSale.isSavedUnfulfilled,
  ],
  (
    status,
    finals,
    payments,
    customer,
    lineItems,
    payAccountBalance,
    customerBalances,
    isSavedUnfulfilled
  ) => ({
    status,
    finals,
    payments,
    customer,
    lineItems,
    payAccountBalance,
    customerBalances,
    isSavedUnfulfilled,
  })
)

const getAbpPaymentTypeMessage = (t: ReturnType<typeof useT>) => {
  return t(
    'These payment types can\u2019t be used for an on account balance payment',
    {
      _context:
        'customer payment type message: on account ABP added but not paid',
    }
  )
}

export const useCustomerPaymentTypeMessage = (
  amountTendered: string | number
): string | null => {
  const t = useT()
  const saleData = useAppSelector(dataSelector)

  const shouldEnforceOnAccountLimit = getFeatureFromCache(
    `enable_on_account_limit_enforcement`
  )

  const currentBalance = getOutstandingBalance(saleData)
  const hasValidTenderedAmount = isFinite(amountTendered)
  const remainingAmountIfTendered =
    hasValidTenderedAmount && isFinite(currentBalance)
      ? subtract(currentBalance, amountTendered)
      : 0

  const isPartialPayment = remainingAmountIfTendered > 0 && currentBalance > 0

  /*
   * If the sale has an Account Balance Payment added but not paid
   */
  if (isABPAddedButNotPaid(saleData)) {
    return getAbpPaymentTypeMessage(t)
  }

  /*
   * If the sale is a service sale
   */
  if (isLegacyServiceSale(saleData.status)) {
    return t('Layaway and on account can\u2019t be used for service sales', {
      _context: 'customer payment type message: service sale',
    })
  }

  /*
   * If the sale is an unfulfilled sale
   */
  if (isMarkedAsUnfulfilled(saleData.status)) {
    const isLaybyAndOnAccountDisabled =
      (saleData.isSavedUnfulfilled || isPartialPayment) && hasPayments(saleData)
    return isLaybyAndOnAccountDisabled
      ? t('Layaway and on account can\u2019t be used for unfulfilled sales', {
          _context:
            'customer payment type message: unfulfilled sale with layby and on account',
        })
      : t('Layaway can\u2019t be used for unfulfilled sales', {
          _context:
            'customer payment type message: unfulfilled sale with layby',
        })
  }

  /*
   * If the sale has a partial payment
   */
  if (
    isPartialPayment &&
    hasValidTenderedAmount &&
    isValidCustomer(saleData.customer)
  ) {
    return t('Layaway and on account can\u2019t be used for partial payments', {
      _context: 'customer payment type message: partial payment',
    })
  }

  /*
   * If the sale has a gift card line item
   */
  if (containsGiftCard(saleData.lineItems)) {
    return t('Layaway can\u2019t be used for gift cards', {
      _context: 'customer payment type message: gift card',
    })
  }

  /*
   * If the on account limit is enforced and the sale is not a layby sale
   */
  if (
    shouldEnforceOnAccountLimit &&
    isValidCustomer(saleData.customer) &&
    !isLayby(saleData.status)
  ) {
    if (isOnAccountLimitDisabled(saleData.customer)) {
      return t(
        'On account payments are disabled for this customer. Select a different payment method or enable on account payments.',
        {
          _context:
            'customer payment type message: on account limit disabled, can update on account limit',
        }
      )
    }

    if (
      !isOnAccountLimitUnlimited(saleData.customer) &&
      isOnAccountLimitReached(saleData)
    ) {
      return t(
        'On account limit reached. Select a different payment method or increase the limit.',
        {
          _context:
            'customer payment type message: on account limit reached, can update on account limit',
        }
      )
    }
  }

  return null
}
